import ArrowLeft from "@mui/icons-material/ArrowLeft";
import { FormattedMessage } from "react-intl";

import { DesignWorkflowClient } from "@citrine/resources/design-workflow";
import { ProjectClient } from "@citrine/resources/project";

import { QuerySuspense } from "#src/components/query-suspense";
import { useBranchName } from "#src/hooks/use-branch-name";

import * as sx from "./project-toolbar.sx";

interface ProjectToolbarProps {
  projectId?: string;
  projectName: string;
  branchId?: string;
  branchName?: string;
  children?: React.ReactNode;
  workspaceId?: string;
}

function ProjectToolbarView({
  branchId,
  branchName,
  children,
  projectId,
  projectName,
  workspaceId,
}: ProjectToolbarProps) {
  return (
    <sx.Toolbar data-testid="project-toolbar">
      <sx.Container>
        {workspaceId ? (
          <sx.HeaderLink
            to={`/projects/${projectId}?active-branch=${branchId}`}
          >
            <sx.MainHeader>
              <sx.CallSplitIcon fontSize="small" />
              <sx.Title data-testid="project-title" component="span">
                {branchName}
              </sx.Title>
            </sx.MainHeader>
            <sx.Description>
              <ArrowLeft fontSize="small" />
              <sx.DescriptionText data-testid="project-description">
                <sx.DescriptionPrefix>
                  <FormattedMessage id="resources.project" />
                  {": "}
                </sx.DescriptionPrefix>
                {projectName}
              </sx.DescriptionText>
            </sx.Description>
          </sx.HeaderLink>
        ) : (
          <sx.HeaderLink to={`/projects/${projectId}`}>
            <sx.Title data-testid="project-title">{projectName}</sx.Title>
          </sx.HeaderLink>
        )}
      </sx.Container>
      {children}
    </sx.Toolbar>
  );
}

export function ProjectToolbar({
  projectId,
  workspaceId,
  children,
}: {
  projectId: string;
  workspaceId?: string;
  children?: React.ReactNode;
}) {
  const projectQuery = ProjectClient.getInstance().useGet(projectId);
  const workspaceQuery =
    DesignWorkflowClient.getInstance(projectId).useGet(workspaceId);
  const branchName = useBranchName(projectId, workspaceQuery.data);

  return (
    <QuerySuspense
      queries={[projectQuery]}
      render={([project]) =>
        project.name ? (
          <ProjectToolbarView
            projectId={projectId}
            projectName={project.name}
            branchId={workspaceQuery.data?.branch_id}
            branchName={branchName}
            workspaceId={workspaceId}
          >
            {children}
          </ProjectToolbarView>
        ) : null
      }
    />
  );
}
